import { lazy, useEffect, useState } from 'react';

import find from 'lodash/find';
import omit from 'lodash/omit';
import without from 'lodash/without';
import { useDispatch, useSelector } from 'react-redux';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useRouteMatch,
} from 'react-router-dom';
import {
  getTimezone,
  IAddress,
  IAutoCoverageVariables,
  IAutoQuote,
  IAutoQuoteEnrollment,
  TCoverageKeys,
  IAutoVariablesPayload,
} from 'sure-platform-sdk';

import api from '../../api';
import QAAutoQuickPurchaseScreen from '../../components/__qa_only__/QAAutoQuickPurchaseScreen';
import { TGetLinksQuote } from '../../interfaces';
import { useWhitelabelConfig } from '../../services/useWhitelabelConfig';
import type { AppStoreState } from '../../store';
import {
  clearOktaForQA,
  clearQuote,
  getAutoQuote,
  getAutoQuoteCheckout,
  getAutoQuoteVars,
  updateQuote,
} from '../../store/actions';
import { getGeocodedNormalizedAddress } from '../../utils';
import withGlobals, { TWithGlobals } from '../../withGlobals';

const QAAutoStartScreen = lazy(
  () => import('../../components/__qa_only__/QAAutoStartScreen')
);
const QARentersStartScreen = lazy(
  () => import('../../components/__qa_only__/QARentersStartScreen')
);

type TQAAutoStartScreensContainer = {} & Pick<TWithGlobals, 'onError'>;

function QAAutoStartScreensContainer({
  onError,
}: TQAAutoStartScreensContainer) {
  const {
    wlConfig: {
      featureFlags: {
        isAutoQuoteLinkExchangeEnabled,
        isRentersQuoteCreationEnabled,
      },
      tenantDisplayName,
    },
  } = useWhitelabelConfig();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();
  const match = useRouteMatch();

  const quote = useSelector((state: AppStoreState) => state.quote);
  const quoteData = useSelector((state: AppStoreState) => state.quoteData);
  const hasOktaInfo = useSelector(
    (state: AppStoreState) =>
      !!state.oktaLoginInfo ||
      !!state.oktaAccessToken ||
      !!state.oktaIdToken ||
      !!state.authToken
  );

  useEffect(() => {
    api.setAuthHeader(process.env.REACT_APP_CARRIER_SECRET_TOKEN || '');
  }, []);

  useEffect(
    function resetQuote() {
      if (
        !!quote ||
        !!quoteData.partnerData ||
        !!quoteData.hasSeenTenantDiscountToast
      ) {
        clearQuote()(dispatch);
      }
    },
    [dispatch, quote, quoteData]
  );

  useEffect(
    function resetLoginInfo() {
      if (hasOktaInfo) {
        clearOktaForQA()(dispatch);
      }
    },
    [dispatch, hasOktaInfo]
  );

  function getAddressObject({
    streetAddress,
    addressLine2,
  }: {
    streetAddress: string;
    addressLine2: string;
  }): Promise<IAddress> {
    return getGeocodedNormalizedAddress(streetAddress, {
      withLatLng: true,
    }).then((address) => {
      return getTimezone(address.latitude, address.longitude).then(
        ({ timeZoneId }) => {
          const cleanAddress = omit(address, [
            'latitude',
            'longitude',
          ]) as IAddress;
          return {
            ...cleanAddress,
            line2: addressLine2 || null,
            timezone: timeZoneId,
          };
        }
      );
    });
  }

  function getLinks(getLinksQuote: TGetLinksQuote) {
    return new Promise<void>((resolve, reject) => {
      return api
        .getLinkUrl(getLinksQuote)
        .then(({ link_url: nextUrl }) => {
          if (
            window.location.hostname === 'localhost' ||
            window.location.hostname.indexOf('sure.town') !== -1
          ) {
            history.push(nextUrl.split('.com')[1]);
          } else {
            window.location.href = nextUrl;
          }
          resolve();
        })
        .catch((error) => {
          onError(error);
          reject();
        });
    });
  }

  function getQuoteVariables({ variables }: IAutoVariablesPayload) {
    const keys: TCoverageKeys[] = [
      'bodily_injury_limit',
      'property_damage_limit',
      'underinsured_motorist_bodily_injury_limit',
      'underinsured_motorist_property_damage_limit',
      'uninsured_motorist_bodily_injury_limit',
      'uninsured_motorist_property_damage_limit',
    ];
    const updatedVariables: Partial<IAutoCoverageVariables> = {};
    keys.forEach((key: TCoverageKeys) => {
      updatedVariables[key] = variables![key].values[0] as any;
    });
    return updatedVariables as IAutoCoverageVariables;
  }

  function getUpdatedQuoteFromEnrollmentApplication(
    autoQuote: IAutoQuote,
    variables: IAutoVariablesPayload
  ) {
    const pniDriver = find(autoQuote.application.drivers, { role: 'pni' })!;
    const primaryHolder = find(autoQuote.application.holders, {
      position_on_policy: 1,
    })!;

    const updatedDriver = {
      ...pniDriver,
      ...autoQuote.application.drivers[0],
      has_sr_22_response: false,
      revoked_suspended_license: false,
    };

    const updatedDrivers = [
      ...without(autoQuote.application.drivers, pniDriver),
      updatedDriver,
    ];

    const updatedHolder = {
      ...primaryHolder,
      ...autoQuote.application.holders[0],
    };

    const updatedHolders = [
      ...without(autoQuote.application.holders, primaryHolder),
      updatedHolder,
    ];

    const updatedQuote = {
      ...quote,
      application: {
        ...autoQuote.application,
        drivers: updatedDrivers,
        holders: updatedHolders,
        variables: getQuoteVariables(variables),
      },
    };

    return updatedQuote;
  }

  function createQuote(enrollmentApplication: IAutoQuoteEnrollment) {
    setIsSubmitting(true);
    api
      .createQuote(enrollmentApplication)
      .then((newQuote) => getQuoteUntilAdpfResolved(newQuote.quote_number));
  }

  function getQuoteUntilAdpfResolved(quoteNumber: string) {
    getAutoQuote(quoteNumber)(dispatch)
      .then((autoQuote) => {
        const adpf = autoQuote.application.metadata.third_party_data_orders[0];

        switch (adpf.status) {
          case 'ready':
          case 'pending':
            setTimeout(() => getQuoteUntilAdpfResolved(quoteNumber), 4000);
            break;
          case 'success':
            createPurchase(autoQuote);
            break;
          default:
            setIsSubmitting(false);
            break;
        }
      })
      .catch(onError);
  }

  function createPurchase(autoQuote: IAutoQuote) {
    getAutoQuoteVars(autoQuote.quote_number)(dispatch)
      .then((variables) => {
        const updatedQuote = getUpdatedQuoteFromEnrollmentApplication(
          autoQuote,
          variables
        );
        updateQuote<IAutoQuote>(
          autoQuote.quote_number,
          updatedQuote as IAutoQuote
        )(dispatch).then((newQuote) => {
          getAutoQuoteCheckout(
            newQuote.quote_number,
            updatedQuote.application
          )(dispatch).then(() => {
            setIsSubmitting(false);
            history.push(`/auto/quotes/${newQuote.quote_number}/review`);
          });
        });
      })
      .catch(onError);
  }

  return (
    <Switch>
      {isAutoQuoteLinkExchangeEnabled && (
        <Route
          exact
          path={`${match.path}auto/start`}
          render={() => (
            <QAAutoStartScreen
              {...{ getAddressObject, getLinks, tenantDisplayName }}
            />
          )}
        />
      )}
      {isRentersQuoteCreationEnabled && (
        <Route
          exact
          path={`${match.path}renters/start`}
          render={() => (
            <QARentersStartScreen
              getAddressObject={getAddressObject}
              getLinks={getLinks}
            />
          )}
        />
      )}
      <Route
        exact
        path={`${match.path}auto/quick-purchase`}
        render={() => (
          <QAAutoQuickPurchaseScreen {...{ createQuote, isSubmitting }} />
        )}
      />
      <Redirect to="/" />
    </Switch>
  );
}

export default withGlobals(QAAutoStartScreensContainer);
